/**
 * BulkUploadView
 *
 * Phase 3: Extract the modal markup into a separate view module so the controller
 * can focus on behavior. For now this is a thin wrapper that returns the same
 * HTML as before. Follow‑up steps will move event binding here.
 */

export function createBulkUploadContent() {
  return `
      <div class="bulk-upload-container">
        <!-- Step 1: File Upload -->
        <div class="upload-step" id="step-upload">
          <h4>Step 1: Select File</h4>
          
          <div class="upload-drop-zone" id="bulk-drop-zone">
            <div class="drop-zone-content">
              <div class="drop-zone-icon">📁</div>
              <h3>Drop CSV file here</h3>
              <p>or use the button below</p>
              <input 
                type="file" 
                id="bulk-file-input" 
                accept=".csv,.xlsx,.xls"
                hidden
              />
              <button class="btn btn-primary" id="browse-file">
                Choose File
              </button>
              <div class="supported-formats">
                Supported: CSV, Excel (XLSX, XLS) - Max 5MB
              </div>
            </div>
            <div class="drop-zone-active" style="display: none;">
              <div class="drop-zone-icon">⬇️</div>
              <p>Drop file here</p>
            </div>
          </div>
          
          <div class="upload-info">
            <p><strong>🤖 AI-Enhanced Upload:</strong> Upload any CSV format. AI will automatically fix missing fields, improve objectives, and structure your initiatives properly.</p>
          </div>
        </div>
        
        <!-- Step 2: Preview & Validation -->
        <div class="upload-step" id="step-preview" style="display: none;">
          <h4>Step 2: Preview & Validate</h4>
          
          <div class="preview-stats" id="preview-stats"></div>
          
          <div class="preview-controls">
            <label>
              <input type="checkbox" id="select-all-rows" checked>
              Select All Valid Rows
            </label>
            <button class="btn btn-small" id="check-duplicates">
              🔍 Check for Duplicates
            </button>
          </div>
          
          <div class="preview-table-container">
            <table class="preview-table" id="preview-table">
              <thead id="preview-header"></thead>
              <tbody id="preview-body"></tbody>
            </table>
          </div>
          
          <div class="validation-errors" id="validation-errors"></div>
        </div>
        
        <!-- Step 3: Duplicate Check -->
        <div class="upload-step" id="step-duplicates" style="display: none;">
          <h4>Step 3: Review Duplicates</h4>
          
          <div class="duplicate-summary" id="duplicate-summary"></div>
          
          <div class="duplicate-list" id="duplicate-list"></div>
        </div>
        
        <!-- Step 4: Import -->
        <div class="upload-step" id="step-import" style="display: none;">
          <h4>Step 4: Import Confirmation</h4>
          
          <div class="import-summary" id="import-summary"></div>
          
          <div class="import-actions">
            <button class="btn btn-primary btn-large" id="confirm-import">
              ✅ Import Selected Initiatives
            </button>
          </div>
        </div>
        
        <!-- Step 5: Complete -->
        <div class="upload-step" id="step-complete" style="display: none;">
          <h4>Step 5: Import Complete! 🎉</h4>
          
          <div class="completion-summary" id="completion-summary"></div>
          
          <div class="completion-actions">
            <button class="btn btn-primary btn-large" id="close-upload">
              ✅ Done - Close Window
            </button>
          </div>
        </div>
        
        <!-- Progress -->
        <div class="upload-progress-backdrop" id="upload-progress-backdrop" style="display:none;"></div>
        <div class="upload-progress" id="upload-progress" style="display: none;">
          <div class="progress-bar">
            <div class="progress-fill" id="progress-fill"></div>
          </div>
          <div class="progress-text" id="progress-text">Processing...</div>
        </div>
      </div>
    `;
}

/**
 * Bind event handlers to the Bulk Upload view and delegate to controller
 * Controller should implement:
 *  - onFileSelected(file)
 *  - onToggleAllRows(checked)
 *  - onCheckDuplicates()
 *  - onConfirmImport()
 *  - onClose()
 */
export function bindBulkUploadHandlers(controller) {
  const dropZone = document.getElementById('bulk-drop-zone');
  const fileInput = document.getElementById('bulk-file-input');
  const browseBtn = document.getElementById('browse-file');

  // Browse button
  browseBtn?.addEventListener('click', () => fileInput?.click());

  // File input change
  fileInput?.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (file && controller?.onFileSelected) controller.onFileSelected(file);
  });

  // Drag & drop
  if (dropZone) {
    let dragCounter = 0;
    dropZone.addEventListener('dragenter', (e) => {
      e.preventDefault();
      dragCounter++;
      dropZone.classList.add('drag-over');
      dropZone.querySelector('.drop-zone-content').style.display = 'none';
      dropZone.querySelector('.drop-zone-active').style.display = 'flex';
    });
    dropZone.addEventListener('dragover', (e) => e.preventDefault());
    dropZone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        dropZone.classList.remove('drag-over');
        dropZone.querySelector('.drop-zone-content').style.display = 'flex';
        dropZone.querySelector('.drop-zone-active').style.display = 'none';
      }
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dragCounter = 0;
      dropZone.classList.remove('drag-over');
      dropZone.querySelector('.drop-zone-content').style.display = 'flex';
      dropZone.querySelector('.drop-zone-active').style.display = 'none';
      const files = e.dataTransfer?.files;
      if (files && files.length > 0 && controller?.onFileSelected) {
        controller.onFileSelected(files[0]);
      }
    });
  }

  // Select all
  document.getElementById('select-all-rows')?.addEventListener('change', (e) => {
    controller?.onToggleAllRows?.(e.target.checked);
  });

  // Duplicates
  document.getElementById('check-duplicates')?.addEventListener('click', () => {
    controller?.onCheckDuplicates?.();
  });

  // Confirm import
  document.getElementById('confirm-import')?.addEventListener('click', () => {
    controller?.onConfirmImport?.();
  });

  // Close
  document.getElementById('close-upload')?.addEventListener('click', () => {
    controller?.onClose?.();
  });
}

export default { createBulkUploadContent, bindBulkUploadHandlers };
